import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { Loader2, Check, X, Eye, FileText, Building2 } from "lucide-react";
import { toast } from "sonner";

type Payment = {
  id: string;
  user_id: string;
  user_email: string | null;
  plan_id: string;
  amount: number;
  currency: string;
  receipt_url: string;
  sender_name: string | null;
  transaction_ref: string | null;
  contact_phone: string | null;
  user_notes: string | null;
  status: string;
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  duration_days: number;
};

export default function AdminPayments() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [viewing, setViewing] = useState<Payment | null>(null);
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectMode, setRejectMode] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("manual_payments")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error("Could not load payments", { description: error.message });
    setPayments((data as Payment[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openReceipt = async (p: Payment) => {
    setViewing(p);
    setRejectMode(false);
    setRejectReason("");
    const { data } = await supabase.storage
      .from("payment-receipts")
      .createSignedUrl(p.receipt_url, 600);
    setReceiptUrl(data?.signedUrl ?? null);
  };

  const approve = async (id: string) => {
    setActionLoading(true);
    try {
      const { error } = await supabase.rpc("approve_manual_payment", { p_payment_id: id });
      if (error) throw error;
      toast.success("Payment approved - plan activated");
      setViewing(null);
      load();
    } catch (e) {
      toast.error("Approve failed", { description: e instanceof Error ? e.message : "" });
    } finally {
      setActionLoading(false);
    }
  };

  const reject = async (id: string) => {
    if (!rejectReason.trim()) {
      toast.error("Please enter a rejection reason");
      return;
    }
    setActionLoading(true);
    try {
      const { error } = await supabase.rpc("reject_manual_payment", {
        p_payment_id: id,
        p_reason: rejectReason,
      });
      if (error) throw error;
      toast.success("Payment rejected");
      setViewing(null);
      load();
    } catch (e) {
      toast.error("Reject failed", { description: e instanceof Error ? e.message : "" });
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = payments.filter((p) => p.status === tab);

  const statusBadge = (s: string) => {
    if (s === "pending") return <Badge variant="secondary">Pending</Badge>;
    if (s === "approved") return <Badge className="bg-success">Approved</Badge>;
    return <Badge variant="destructive">Rejected</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" /> Bank Transfer Payments
        </CardTitle>
        <CardDescription>Review user-submitted bank transfer receipts and approve or reject them.</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="pending">
              Pending ({payments.filter((p) => p.status === "pending").length})
            </TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-4">
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No {tab} payments.</p>
            ) : (
              <div className="space-y-2">
                {filtered.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between gap-3 p-3 rounded-md border hover:bg-muted/50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium truncate">{p.user_email || p.user_id}</p>
                        {statusBadge(p.status)}
                        <Badge variant="outline" className="uppercase">{p.plan_id}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        ${p.amount} {p.currency} · {new Date(p.created_at).toLocaleString()}
                        {p.sender_name && ` · ${p.sender_name}`}
                        {p.transaction_ref && ` · Ref: ${p.transaction_ref}`}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openReceipt(p)}>
                      <Eye className="h-4 w-4 mr-1" /> Review
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payment Review</DialogTitle>
            <DialogDescription>Review the receipt before approving or rejecting.</DialogDescription>
          </DialogHeader>

          {viewing && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2 text-sm">
                <Row label="User" value={viewing.user_email || viewing.user_id} />
                <Row label="Plan" value={viewing.plan_id.toUpperCase()} />
                <Row label="Amount" value={`$${viewing.amount} ${viewing.currency}`} />
                <Row label="Sender" value={viewing.sender_name || "-"} />
                <Row label="Transaction Ref" value={viewing.transaction_ref || "-"} />
                <Row label="Phone" value={viewing.contact_phone || "-"} />
                <Row label="Submitted" value={new Date(viewing.created_at).toLocaleString()} />
                <Row label="Duration on approve" value={`${viewing.duration_days} days`} />
                {viewing.user_notes && (
                  <div>
                    <p className="text-xs text-muted-foreground">Notes</p>
                    <p className="text-sm whitespace-pre-wrap">{viewing.user_notes}</p>
                  </div>
                )}
                {viewing.admin_notes && (
                  <div>
                    <p className="text-xs text-muted-foreground">Admin Notes</p>
                    <p className="text-sm whitespace-pre-wrap">{viewing.admin_notes}</p>
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Receipt</p>
                {receiptUrl ? (
                  receiptUrl.match(/\.pdf/i) ? (
                    <a href={receiptUrl} target="_blank" rel="noopener" className="flex items-center gap-2 p-3 border rounded-md hover:bg-muted">
                      <FileText className="h-4 w-4" /> View PDF
                    </a>
                  ) : (
                    <a href={receiptUrl} target="_blank" rel="noopener">
                      <img src={receiptUrl} alt="Receipt" className="rounded-md border max-h-96 w-full object-contain bg-white" />
                    </a>
                  )
                ) : (
                  <Loader2 className="h-5 w-5 animate-spin" />
                )}
              </div>
            </div>
          )}

          {viewing?.status === "pending" && (
            <>
              {rejectMode && (
                <div>
                  <Textarea
                    placeholder="Reason for rejection (shown to user)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={2}
                  />
                </div>
              )}
              <DialogFooter className="gap-2">
                {!rejectMode ? (
                  <>
                    <Button variant="outline" onClick={() => setRejectMode(true)} disabled={actionLoading}>
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                    <Button onClick={() => approve(viewing!.id)} disabled={actionLoading}>
                      {actionLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                      Approve & Activate Plan
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setRejectMode(false)} disabled={actionLoading}>
                      Cancel
                    </Button>
                    <Button variant="destructive" onClick={() => reject(viewing!.id)} disabled={actionLoading}>
                      {actionLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                      Confirm Rejection
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 border-b py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right break-all">{value}</span>
    </div>
  );
}
